import { logger } from "../../utils/logger";
import { showWebCryptoUnavailableToast } from "../../utils/browserCapabilityToasts";

export async function sha256Hex(data: string | ArrayBuffer | Uint8Array): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  const bytes =
    typeof data === "string"
      ? new TextEncoder().encode(data)
      : data instanceof Uint8Array
        ? data
        : new Uint8Array(data);
  if (!subtle) {
    // Fallback for runtimes where SubtleCrypto is unavailable (older/non-secure embedded webviews).
    // Keep env export working with deterministic, non-cryptographic fingerprints.
    logger.warn("[env-export] crypto.subtle unavailable; using fallback deterministic hash");
    showWebCryptoUnavailableToast("Environment export");
    return weakDeterministicHashHex(bytes);
  }
  const buffer = bytes.buffer.slice(
    bytes.byteOffset,
    bytes.byteOffset + bytes.byteLength
  ) as ArrayBuffer;
  const digest = await subtle.digest("SHA-256", buffer);
  const arr = Array.from(new Uint8Array(digest));
  return arr.map((b) => b.toString(16).padStart(2, "0")).join("");
}

function weakDeterministicHashHex(bytes: Uint8Array): string {
  // 4 parallel FNV-1a 64-bit lanes, concatenated => 256-bit hex string.
  // This is NOT cryptographic; it exists only to avoid export failure.
  const OFF = 0xcbf29ce484222325n;
  const PRIME = 0x100000001b3n;
  const MASK = 0xffffffffffffffffn;
  let h0 = OFF ^ 0x01n;
  let h1 = OFF ^ 0x02n;
  let h2 = OFF ^ 0x03n;
  let h3 = OFF ^ 0x04n;
  for (let i = 0; i < bytes.length; i += 1) {
    const b = BigInt(bytes[i]);
    h0 = ((h0 ^ b) * PRIME) & MASK;
    h1 = ((h1 ^ (b + 0x9en)) * PRIME) & MASK;
    h2 = ((h2 ^ (b + 0x4dn)) * PRIME) & MASK;
    h3 = ((h3 ^ (b + 0xd3n)) * PRIME) & MASK;
  }
  const toHex64 = (n: bigint) => n.toString(16).padStart(16, "0");
  return `${toHex64(h0)}${toHex64(h1)}${toHex64(h2)}${toHex64(h3)}`;
}
